import {observer} from 'mobx-react-lite';
import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {ESmallButton} from 'webapp-lib/Components';

import {Dialog} from '#/Dialog/Dialog';
import Loading from '#/Loading';
import {PdfViewer, PdfViewerProps} from '#/PdfViewer/PdfViewer';
import {UnknownDocument} from '_/assets/images';
import {I18NextNS} from '_/i18n';
import {isElectron} from '_/utilities';

import {ErrorView, PreviewActionRow, StyledPreviewDialog} from './styles';

export type PreviewDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  openActionDisabled?: boolean;
  onOpenRequest?: () => void | undefined;
  errorMessage?: string;
  isLoading?: boolean;
} & Omit<PdfViewerProps, 'onExpand'>;

const UPreviewDialog = ({
  isOpen,
  onClose,
  openActionDisabled = false,
  onOpenRequest,
  errorMessage,
  isLoading = false,
  ...props
}: PreviewDialogProps): React.ReactElement => {
  const {t} = useTranslation<I18NextNS>('common');
  const handleOpenRequest = useCallback(
    () => onOpenRequest?.(),
    [onOpenRequest]
  );

  const errorView = (
    <ErrorView>
      <p className='title'>{t('errors.noPreviewAvailable')}</p>
      <p>
        {errorMessage
          ? errorMessage
          : t('errors.noPreviewAvailableInformation')}
      </p>
    </ErrorView>
  );

  return (
    <Dialog isShown={isOpen} onClose={onClose}>
      <StyledPreviewDialog>
        <PreviewActionRow>
          {onOpenRequest && (
            <ESmallButton
              disabled={!isElectron() || isLoading || openActionDisabled}
              Icon={UnknownDocument}
              onClick={handleOpenRequest}
            >
              {t('actions.openFile')}
            </ESmallButton>
          )}
        </PreviewActionRow>
        {isLoading ? (
          <Loading noDelay />
        ) : (
          props.file && <PdfViewer {...props} error={errorView} width={620} />
        )}
      </StyledPreviewDialog>
    </Dialog>
  );
};

export const PreviewDialog = observer(UPreviewDialog);
